class WebSocketManager {
  constructor() {
    this.ws = null;
    this.baseUrl = '';
    this.url = '';
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = Infinity; // Reconnect indefinitely
    this.reconnectDelay = 1000; // start with 1 second
    this.maxReconnectDelay = 30000; // max 30 seconds
    this.isConnected = false;
    this.listeners = new Set();
    this.intentionalClose = false;
  }

  connect(url) {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    let base = url || (import.meta.env.VITE_API_BASE ? import.meta.env.VITE_API_BASE.replace(/^http/, 'ws') + '/ws' : `${protocol}//${window.location.host}/ws`);
    this.intentionalClose = false;

    // Fallback if VITE_API_BASE is like "/api"
    if (base.startsWith('/api')) {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      base = `${protocol}//${window.location.host}/ws`;
    } else if (base.startsWith('http://localhost:8080/api')) {
      base = 'ws://localhost:8080/ws';
    }

    // The shop used to be appended here as `?orgId=`. The server no longer
    // takes it from the address — it reads the token instead — so only the
    // address without credentials is kept, and the token is added per attempt
    // in _connect().
    this.baseUrl = base;
    this.url = base;

    this._connect();
  }

  _connect() {
    if (this.ws && (this.ws.readyState === WebSocket.CONNECTING || this.ws.readyState === WebSocket.OPEN)) {
      return;
    }

    // Built fresh on every attempt, including reconnections. An access token
    // lasts an hour and this page stays open far longer, so a URL built once at
    // connect() would carry an expired token into every later reconnect and be
    // refused from then on.
    const token = localStorage.getItem('org.accessToken');
    if (!token) {
      if (!this.intentionalClose) {
        this._reconnect();
      }
      return;
    }
    this.url = `${this.baseUrl}?token=${encodeURIComponent(token)}`;

    try {
      this.ws = new WebSocket(this.url);

      this.ws.onopen = () => {
        console.log('[WebSocket] Connected');
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000;
        this._notifyListeners({ type: 'STATUS', status: 'connected' });
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this._notifyListeners(data);
        } catch {
          // If it's not JSON, just broadcast as plain text
          this._notifyListeners({ type: 'MESSAGE', data: event.data });
        }
      };

      this.ws.onclose = () => {
        console.log('[WebSocket] Disconnected');
        this.isConnected = false;
        this.ws = null;
        this._notifyListeners({ type: 'STATUS', status: 'disconnected' });

        if (!this.intentionalClose) {
          this._reconnect();
        }
      };

      this.ws.onerror = (error) => {
        console.error('[WebSocket] Error:', error);
        // The onclose event will fire immediately after onerror, triggering reconnect
      };
    } catch (err) {
      console.error('[WebSocket] Initialization error:', err);
      if (!this.intentionalClose) {
        this._reconnect();
      }
    }
  }

  _reconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[WebSocket] Max reconnection attempts reached. Please refresh the page.');
      this._notifyListeners({ type: 'STATUS', status: 'failed' });
      return;
    }

    console.log(`[WebSocket] Reconnecting in ${this.reconnectDelay}ms (Attempt ${this.reconnectAttempts + 1})`);
    setTimeout(() => {
      this.reconnectAttempts++;
      // Exponential backoff
      this.reconnectDelay = Math.min(this.reconnectDelay * 2, this.maxReconnectDelay);
      this._connect();
    }, this.reconnectDelay);
  }

  disconnect() {
    this.intentionalClose = true;
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.isConnected = false;
    this._notifyListeners({ type: 'STATUS', status: 'disconnected' });
  }

  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  _notifyListeners(data) {
    this.listeners.forEach(listener => listener(data));
  }
}

// Export a singleton instance
export const wsManager = new WebSocketManager();
